import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { User, UserRole } from '../models/user.model';
import { KeycloakApiService } from './keycloak-api.service';

/** Auth Service — dev simulation + production credential login */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly keycloakApi = inject(KeycloakApiService);
  private readonly _currentUser = signal<User | null>(null);

  constructor() {
    this.restoreSession();
  }

  readonly currentUser = this._currentUser.asReadonly();
  readonly isLoggedIn = computed(() => this._currentUser() !== null);
  readonly isStudent = computed(() => this.hasRole('STUDENT'));
  readonly isAdmin = computed(() => this.hasRole('ADMIN') || this.hasRole('INSTRUCTOR'));
  readonly isSuperAdmin = computed(() => this.hasRole('SUPER_ADMIN'));

  /** Restore auth state from sessionStorage on page refresh */
  private restoreSession(): void {
    const token = sessionStorage.getItem('access_token');
    if (token) {
      const user = this.decodeUserFromToken(token);
      if (user) {
        this._currentUser.set(user);
        if (this.isTokenExpired(token) && this.getRefreshToken()) {
          void this.refreshAccessToken();
        }
        return;
      }
    }
    const devUser = sessionStorage.getItem('dev_user');
    if (devUser) {
      try {
        this._currentUser.set(JSON.parse(devUser));
      } catch {
        sessionStorage.removeItem('dev_user');
      }
    }
  }

  /** Simulate login with a specific role for development */
  loginAs(role: UserRole): void {
    const users: Record<UserRole, User> = {
      STUDENT: {
        id: 'usr-student-01',
        email: 'rahul@example.com',
        name: 'Rahul Kumar',
        username: 'rahul.kumar',
        roles: ['STUDENT'],
        avatarUrl: 'https://ui-avatars.com/api/?name=Rahul+Kumar&background=6366f1&color=fff&size=40',
      },
      ADMIN: {
        id: 'usr-admin-01',
        email: 'arjun@example.com',
        name: 'Dr. Arjun Mehta',
        username: 'arjun.mehta',
        roles: ['ADMIN', 'INSTRUCTOR'],
        avatarUrl: 'https://ui-avatars.com/api/?name=Arjun+Mehta&background=6366f1&color=fff&size=40',
      },
      INSTRUCTOR: {
        id: 'usr-admin-01',
        email: 'arjun@example.com',
        name: 'Dr. Arjun Mehta',
        username: 'arjun.mehta',
        roles: ['ADMIN', 'INSTRUCTOR'],
        avatarUrl: 'https://ui-avatars.com/api/?name=Arjun+Mehta&background=6366f1&color=fff&size=40',
      },
      SUPER_ADMIN: {
        id: 'usr-superadmin-01',
        email: 'superadmin@skillbridge.com',
        name: 'Super Admin',
        username: 'super.admin',
        roles: ['SUPER_ADMIN'],
        avatarUrl: 'https://ui-avatars.com/api/?name=Super+Admin&background=ef4444&color=fff&size=40',
      },
    };
    this._currentUser.set(users[role]);
    sessionStorage.setItem('dev_user', JSON.stringify(users[role]));
  }

  /**
   * Production login — POSTs credentials to the backend.
   * The backend authenticates against Keycloak and returns a signed JWT.
   * We decode the JWT payload to populate the current user.
   */
  async loginWithCredentials(identifier: string, identifierType: 'email' | 'username', password: string): Promise<void> {
    const body = { identifier, password };

    const response = await firstValueFrom(
      this.http.post<{ accessToken: string; refreshToken?: string }>('/api/auth/login', body),
    );

    // Store session first so the interceptor can attach the token to /me
    this.setSession(response.accessToken, response.refreshToken);
    await this.refreshProfile();

    if (!this._currentUser()) {
      throw new Error('Unable to load user profile. Please contact support.');
    }
  }

  getAccessToken(): string | null {
    return sessionStorage.getItem('access_token');
  }

  /**
   * Fetches the full user profile from /me and refreshes the current user signal.
   * Roles are preserved from the existing signal or decoded from the stored token.
   * Silent no-op if no token is present.
   */
  async refreshProfile(): Promise<void> {
    const token = this.getAccessToken();
    if (!token) return;
    try {
      const me = await firstValueFrom(this.keycloakApi.getMe());
      const roles = this._currentUser()?.roles ?? this.decodeUserFromToken(token)?.roles ?? ['STUDENT' as UserRole];
      this._currentUser.set({
        id: me.id,
        email: me.email,
        name: [me.firstName, me.lastName].filter(Boolean).join(' ') || me.username,
        username: me.username,
        roles,
        contactNumber: me.contactNumber,
        bio: me.bio,
        avatarUrl: `https://ui-avatars.com/api/?name=${encodeURIComponent([me.firstName, me.lastName].filter(Boolean).join(' '))}&background=6366f1&color=fff&size=40`,
      });
    } catch {
      // keep existing user state on failure
    }
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem('refresh_token');
  }

  async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      return false;
    }

    try {
      const response = await firstValueFrom(
        this.http.post<{ accessToken: string; refreshToken?: string }>('/api/auth/refresh', { refreshToken }),
      );

      const user = this.decodeUserFromToken(response.accessToken);
      if (user) {
        this.setSession(response.accessToken, response.refreshToken);
        this._currentUser.set(user);
        return true;
      }
    } catch {
      this.logout();
    }

    return false;
  }

  private setSession(accessToken: string, refreshToken?: string): void {
    sessionStorage.setItem('access_token', accessToken);
    if (refreshToken) {
      sessionStorage.setItem('refresh_token', refreshToken);
    }
  }

  isTokenExpired(token: string): boolean {
    try {
      const payloadB64 = token.split('.')[1];
      const decoded = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
      const exp = decoded?.exp;
      return typeof exp === 'number' ? Date.now() >= exp * 1000 : false;
    } catch {
      return false;
    }
  }

  /** Decode a JWT payload — no signature verification (done server-side) */
  private decodeUserFromToken(token: string): User | null {
    try {
      const payloadB64 = token.split('.')[1];
      const decoded = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
      const realmRoles: string[] = decoded?.realm_access?.roles ?? [];
      
      // Filter and strip "ROLE_" prefix if present
      const roles = realmRoles
        .map(r => r.replace(/^ROLE_/, '')) // Remove ROLE_ prefix if present
        .filter((r): r is UserRole =>
          ['STUDENT', 'ADMIN', 'INSTRUCTOR', 'SUPER_ADMIN'].includes(r),
        );
      
      return {
        id: decoded.sub ?? '',
        email: decoded.email ?? '',
        name: decoded.name ?? decoded.preferred_username ?? decoded.email ?? '',
        username: decoded.preferred_username ?? '',
        roles: roles.length ? roles : ['STUDENT'],
        avatarUrl: `https://ui-avatars.com/api/?name=${encodeURIComponent(decoded.name ?? decoded.email)}&background=6366f1&color=fff&size=40`,
      };
    } catch {
      return null;
    }
  }

  logout(): void {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('dev_user');
    this._currentUser.set(null);
  }

  updateCurrentUser(partial: Partial<{ name: string; email: string; username: string }>): void {
    const user = this._currentUser();
    if (!user) return;
    const updated = { ...user, ...partial };
    this._currentUser.set(updated);
    if (sessionStorage.getItem('dev_user')) {
      sessionStorage.setItem('dev_user', JSON.stringify(updated));
    }
  }

  hasRole(role: UserRole): boolean {
    return this._currentUser()?.roles.includes(role) ?? false;
  }

  /**
   * Get the primary role of the current user based on role priority
   * Priority: SUPER_ADMIN > ADMIN > INSTRUCTOR > STUDENT
   */
  getPrimaryRole(): UserRole | null {
    const user = this._currentUser();
    if (!user) return null;

    if (user.roles.includes('SUPER_ADMIN')) return 'SUPER_ADMIN';
    if (user.roles.includes('ADMIN')) return 'ADMIN';
    if (user.roles.includes('INSTRUCTOR')) return 'INSTRUCTOR';
    if (user.roles.includes('STUDENT')) return 'STUDENT';

    return null;
  }

  /**
   * Get the dashboard route based on the user's primary role
   */
  getDashboardRoute(): string {
    const primaryRole = this.getPrimaryRole();

    switch (primaryRole) {
      case 'SUPER_ADMIN':
        return '/super-admin';
      case 'ADMIN':
      case 'INSTRUCTOR':
        return '/admin/dashboard';
      case 'STUDENT':
      default:
        return '/student/dashboard';
    }
  }
}
